import {
  CommonAssortmentWithCountForStatusTypes,
  FileType,
  InstallationAssortmentStatus,
  PendingUpdateReason,
} from './DTO'

export enum UiStatus {
  ERROR = 'ERROR',
  WARNING = 'WARNING',
  NORMAL_PENDING = 'NORMAL_PENDING',
  UP_TO_DATE = 'UP_TO_DATE',
  JOB_CREATED = 'JOB_CREATED',
  JOB_ONGOING = 'JOB_ONGOING',
  NOT_COMPATIBLE = 'NOT_COMPATIBLE',
  UNKNOWN = 'UNKNOWN',
}

export const allUiStatuses = Object.values(UiStatus)

export type FileStatus = {
  type: FileType
  uiStatus: UiStatus
  cause: string
}

export function getStatusEmoji(uiStatus: UiStatus) {
  switch (uiStatus) {
    case UiStatus.ERROR:
      return '🚨'
    case UiStatus.WARNING:
      return '⚠️'
    case UiStatus.NORMAL_PENDING:
      return '⚡'
    case UiStatus.UP_TO_DATE:
      return '✔️'
    case UiStatus.JOB_CREATED:
      return '⏳'
    case UiStatus.JOB_ONGOING:
      return '🏗'
    case UiStatus.NOT_COMPATIBLE:
      return '🚫'
    case UiStatus.UNKNOWN:
      return '💤'
    default:
      return '💤'
  }
}

export function getStatusText(uiStatus: UiStatus) {
  switch (uiStatus) {
    case UiStatus.ERROR:
      return 'Failed Update'
    case UiStatus.WARNING:
      return 'Cancelled Update'
    case UiStatus.NORMAL_PENDING:
      return 'To be updated'
    case UiStatus.UP_TO_DATE:
      return 'Up to date'
    case UiStatus.JOB_CREATED:
      return 'Job created'
    case UiStatus.JOB_ONGOING:
      return 'Job sent to machine'
    case UiStatus.NOT_COMPATIBLE:
      return 'No crate unit'
    case UiStatus.UNKNOWN:
      return 'Ongoing update > 24h'
    default:
      return 'Unknown status'
  }
}

export function mapBackendStatusToUiStatus(backendStatus: string): UiStatus {
  switch (backendStatus) {
    case 'FAILED':
    case 'RECOVERED':
      return UiStatus.ERROR
    case 'CANCELLED':
      return UiStatus.WARNING
    case 'CREATED':
      return UiStatus.JOB_CREATED
    case 'IN_PROGRESS':
      return UiStatus.JOB_ONGOING
    case 'NOT_COMPATIBLE':
      return UiStatus.NOT_COMPATIBLE
    case 'NORMAL_PENDING':
      return UiStatus.NORMAL_PENDING
    case 'COMPLETED':
      return UiStatus.UP_TO_DATE
    default:
      return UiStatus.UNKNOWN
  }
}

export function fileTypesForCommonAssortmentWithCountForStatusTypes(common: CommonAssortmentWithCountForStatusTypes) {
  return [
    common.shapeFileStream ? 'SHAPE' : '',
    common.barcodeFileStream ? 'BARCODE' : '',
    common.crateFileStream ? 'CRATE' : '',
    common.crateFileStream ? 'LOCAL_CRATE' : '',
    common.artikeldatenFileStream ? 'ARTIKELDATEN' : '',
  ].filter((t) => t !== '')
}

export function getFileStatus(latest: InstallationAssortmentStatus, type: FileType): FileStatus {
  if (latest === undefined) {
    return {
      type: FileType.UNKOWN,
      uiStatus: UiStatus.ERROR,
      cause: '',
    }
  }
  if (!latest.lastUpdateStatus) {
    return {
      type,
      uiStatus: UiStatus.NORMAL_PENDING,
      cause: 'Never received updates',
    }
  }

  if (latest.lastUpdateStatus === 'NOT_COMPATIBLE') {
    return {
      type,
      uiStatus: UiStatus.NOT_COMPATIBLE,
      cause: 'No crate unit',
    }
  }

  if (latest.lastUpdateStatus === 'FAILED' || latest.lastUpdateStatus === 'RECOVERED') {
    return {
      type,
      uiStatus: UiStatus.ERROR,
      cause: 'Machine Update Failed',
    }
  }

  if (latest.lastUpdateStatus === 'CANCELLED') {
    return {
      type,
      uiStatus: UiStatus.WARNING,
      cause: 'Machine Update did not finish on time / manually cancelled',
    }
  }

  if (latest.lastUpdateStatus === 'CREATED' && existsAndIsOlderThan24Hours(latest.lastUpdateStatusTimestamp)) {
    return {
      type,
      uiStatus: UiStatus.UNKNOWN,
      cause: 'No communication with machine for 24 hours',
    }
  }

  if (latest.lastUpdateStatus === 'CREATED') {
    return {
      type,
      uiStatus: UiStatus.JOB_CREATED,
      cause: 'Part of ongoing update job',
    }
  }

  if (latest.lastUpdateStatus === 'IN_PROGRESS' && existsAndIsOlderThan24Hours(latest.lastUpdateStatusTimestamp)) {
    return {
      type,
      uiStatus: UiStatus.UNKNOWN,
      cause: 'No communication with machine for 24 hours',
    }
  }

  if (latest.lastUpdateStatus === 'IN_PROGRESS') {
    return {
      type,
      uiStatus: UiStatus.JOB_ONGOING,
      cause: 'Part of ongoing update job',
    }
  }

  if (latest.pendingUpdateReason === PendingUpdateReason.NEW_FILE_AVAILABLE) {
    return {
      type,
      uiStatus: UiStatus.NORMAL_PENDING,
      cause: 'New File Uploaded',
    }
  }

  if (latest.pendingUpdateReason === PendingUpdateReason.COMMON_ASSORTMENT_MODIFICATIONS) {
    return {
      type,
      uiStatus: UiStatus.NORMAL_PENDING,
      cause: 'Modifications to Common Assortment',
    }
  }

  if (latest.pendingUpdateReason === PendingUpdateReason.INSTALLATION_MODIFICATIONS) {
    return {
      type,
      uiStatus: UiStatus.NORMAL_PENDING,
      cause: 'Modifications to Installation Override',
    }
  }

  return {
    type,
    uiStatus: UiStatus.UP_TO_DATE,
    cause: 'Assortment on machine is up to date',
  }
}

const twentyFourHoursInMillis = 24 * 60 * 60 * 1000
function existsAndIsOlderThan24Hours(timestamp: string | null): boolean {
  if (!timestamp) {
    return false
  }
  return new Date().getTime() - new Date(timestamp).getTime() > twentyFourHoursInMillis
}
